// Layered network-flow renderer for the Overview graph section.
//
//   Clients —(connected to)→ Networks —(having)→ Services —(exposes)→ Tools
//
// Hand-rolled (no graph lib): four columns of node cards with SVG bezier
// connectors drawn between related cards across adjacent columns. Connector
// geometry is measured from the laid-out DOM — see `offsetWithin`, which WALKS the
// offsetParent chain up to `.flow-inner` rather than trusting a node's own
// offsetLeft/Top to be relative to it — so it stays correct as cards wrap, the panel
// filters, nodes collapse, or a new positioned box appears between the two.
//
// Interactions (all in component state):
//   • a single search box filters nodes by name across every column — matches and
//     their whole connected chain stay; everything else is hidden;
//   • a row of HEALTH chips (UX1) filters the same way but by node state — healthy,
//     failed, not running — each carrying its own count, so "is anything broken" is
//     answered before a click and "show me those" after one;
//   • each upstream node card has an expand/collapse toggle that hides the
//     downstream children reached only through it;
//   • a ROUTES layer (H7) overlays the stream/call routes (ADR-020) on the tool
//     nodes — directed segments styled by live USAGE (thickness/color/animation),
//     with an on/off toggle + legend; it polls route-usage + reacts to route.* bus
//     events so a fired route lights up live.
//
// The graph scroll area has a fixed max height like any other Overview block
// (H46 — the H6 stretch-to-viewport-bottom was removed once H36 made the graph no
// longer the last block; the inner graph scrolls within that fixed area).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useStore } from '../store/store';
import {
  useLiveReseed,
  publicationsSig,
  clientsSig,
  routeInstancesSig,
} from '../lib/useLiveReseed';
import { Loading, ErrorState } from './ui';
import { Drawer, DetailRow } from './Drawer';
import type { RouteUsage } from '../api/schemas';
import {
  COLUMNS,
  HEALTH_FILTERS,
  assembleGraph,
  buildRouteOverlay,
  activityByNode,
  healthChain,
  healthCounts,
  matchingChain,
  mergeQueues,
  nodesWithChildren,
  orderColumns,
  queueIndex,
  restrictInput,
  summarizeNodeQueue,
  visibleNodeIds,
  type FlowGraph as FlowGraphData,
  type HealthFilter,
  type FlowNode,
  type GraphInput,
  type NodeActivity,
  type NodeQueueSummary,
  type QueueDepthLike,
  type RouteOverlay,
  type ToolActivity,
} from '../lib/networkGraph';

// --- data loading (assembled from the existing api.* methods) --------------

interface LoadedGraph {
  graph: FlowGraphData;
  networkIds: string[];
  clientQueueDepth: Map<string, number>;
}

interface LoadedInput {
  input: GraphInput;
  queues: QueueDepthLike[];
}

async function loadInput(): Promise<LoadedInput> {
  const [networks, publications, services, clientList, queues] = await Promise.all([
    api.networks(),
    api.publications(),
    api.serviceDefinitions(),
    api.clients(),
    // H37 — per-canonical-tool task queues. A gateway without the endpoint (404) yields none, so
    // the graph just shows no queue figures rather than erroring.
    api.queues().catch(() => ({ items: [] as QueueDepthLike[] })),
  ]);
  // A client's networks come from its provider instances (instance → publication →
  // network). Fetch per-client so clients with no live instances still render.
  const instLists = await Promise.all(clientList.items.map((c) => api.clientInstances(c.id)));
  return {
    input: {
      networks: networks.items,
      publications: publications.items,
      services: services.items,
      clients: clientList.items,
      instances: instLists.flatMap((l) => l.items),
    },
    queues: queues.items,
  };
}

/** Self-contained section: loads + assembles the graph, then renders <FlowGraph/>.
 *
 * `networkIds` (UX18) is the scope the page is asking about — the reader's own
 * networks by default. Passing undefined means "everything the gateway returned",
 * which is what this did before the scope existed. The cut happens on the INPUT, so
 * what reaches the assembler is a smaller but whole topology rather than a graph
 * with edges pointing at nodes that are no longer in it.
 */
export function NetworkFlowSection({ networkIds: scope }: { networkIds?: ReadonlySet<string> } = {}) {
  const res = useResource(loadInput, []);
  // Realtime monitoring WITHOUT polling: seed once, then reconcile the topology QUIETLY (reload(true), so
  // it never flips to the loading spinner) when the event bus signals a real change — a publication.changed
  // or a client.status (node coming up/down) — and on (re)connect so state can't drift. A SINGLE
  // low-frequency reconcile (30s) backstops the parts the bus emits no event for (service-definition edits,
  // provider-instance runtime state). Live CALL activity (pulse) and queue depth are instant from the store.
  useLiveReseed(res.reload, (s) => `${publicationsSig(s)}#${clientsSig(s)}`, { fallbackMs: 30_000 });
  // Re-assemble the graph ONLY when the topology actually changed: a content signature (ids + the few
  // fields the graph renders) keys the memo, so an 8s poll that returns identical data keeps the SAME
  // graph object — no rebuild, no edge re-measure, no flicker. Liveness/queue changes still bump the sig.
  const scopeKey = scope ? [...scope].sort().join(',') : '*';
  const sig = useMemo(() => {
    const inp = res.data?.input;
    if (!inp) return '';
    return JSON.stringify({
      n: inp.networks.map((n) => [n.id, n.slug, n.name, n.status]),
      c: inp.clients.map((c) => [c.id, c.name, c.status, c.queue_depth ?? 0]),
      p: inp.publications.map((p) => [p.id, p.service_slug, p.network_id, p.status, p.service_definition_id, (p.exposed_tool_ids ?? []).join(',')]),
      i: inp.instances.map((x) => [x.client_id, x.service_publication_id]).sort(),
      s: scopeKey,
    });
  }, [res.data, scopeKey]);
  const loaded = useMemo<LoadedGraph>(() => {
    if (!res.data) return { graph: { nodes: [], edges: [] }, networkIds: [], clientQueueDepth: new Map() };
    const input = scope ? restrictInput(res.data.input, scope) : res.data.input;
    const clientQueueDepth = new Map<string, number>();
    for (const c of input.clients) if (c.queue_depth != null) clientQueueDepth.set(c.id, c.queue_depth);
    return {
      graph: assembleGraph(input),
      networkIds: input.networks.map((n) => n.id),
      clientQueueDepth,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Live queue depth: overlay the event-bus queue.changed projection onto the REST seed rows so the
  // per-node queue figures move in real time without refetching (the REST rows still supply running/
  // providers, which the bus omits).
  const liveQueues = useStore((s) => s.queues);
  const queues = useMemo(
    () => mergeQueues(res.data?.queues ?? [], liveQueues),
    [res.data, liveQueues],
  );

  // Only show the loading spinner on the FIRST load — a background refresh keeps the current graph up.
  if (res.loading && !res.data) return <Loading what="Building network flow…" />;
  if (res.error) return <ErrorState message={res.error} onRetry={res.reload} />;
  return (
    <FlowGraph
      graph={loaded.graph}
      networkIds={loaded.networkIds}
      queues={queues}
      clientQueueDepth={loaded.clientQueueDepth}
    />
  );
}

// --- routes-layer live data ------------------------------------------------

/**
 * Fetch route-usage for every network and keep it fresh while the layer is ON — WITHOUT polling. Seeds
 * once when enabled, then refetches whenever a route instance appears/changes on the live bus
 * (route.instantiated/completed/errored) so exercising a route lights it up promptly, and on bus
 * (re)connect so it can't drift. A gateway without the endpoint (404) yields an empty list, so the
 * layer just shows no routes rather than erroring.
 */
function useRouteUsages(networkIds: string[], enabled: boolean): RouteUsage[] {
  const [usages, setUsages] = useState<RouteUsage[]>([]);
  // A signature of route-instance states (not just their count) so completed/errored transitions — not
  // only brand-new instances — trigger a refetch. Distinct from generic heartbeat frames, so we don't
  // refetch on every metrics frame.
  const routeSig = useStore(routeInstancesSig);
  const connected = useStore((s) => s.connected);
  const idsKey = networkIds.join(',');

  useEffect(() => {
    if (!enabled || networkIds.length === 0) {
      setUsages([]);
      return;
    }
    let alive = true;
    const load = async () => {
      const lists = await Promise.all(
        networkIds.map((id) =>
          api.routeUsage(id).catch(() => ({ items: [] as RouteUsage[] })),
        ),
      );
      if (alive) setUsages(lists.flatMap((l) => l.items));
    };
    void load();
    return () => {
      alive = false;
    };
  }, [enabled, idsKey, routeSig, connected]);

  return usages;
}

// --- presentational + interaction component --------------------------------

const toneClass = (t?: FlowNode['tone']) => `tone-${t ?? 'info'}`;

const HEALTH_LABEL: Record<HealthFilter, string> = {
  all: 'All',
  healthy: 'Healthy',
  failed: 'Failed',
  offline: 'Not running',
};

const HEALTH_TITLE: Record<HealthFilter, string> = {
  all: 'Every node',
  healthy: 'Nodes with no recent failure and a live provider',
  failed: 'Nodes with a recent failed call, and offline/rejected ones',
  offline: 'Published and known, but nothing live is serving it right now',
};

/**
 * A node's position in the coordinate space of `root` — the space the SVG draws in.
 *
 * `offsetLeft`/`offsetTop` are measured from the nearest POSITIONED ancestor, not
 * from whichever element you had in mind. The connectors were read straight off
 * them, which was correct only while `.flow-inner` happened to be the only
 * positioned box in the graph; giving the column bodies `position: relative` for the
 * aligned layout silently re-based every measurement onto its own column, and every
 * line moved left by the column's x. So the chain is WALKED instead of assumed: it
 * gives the same answer no matter which ancestors are positioned, today or later.
 *
 * Layout offsets, deliberately — not getBoundingClientRect: the SVG lives inside the
 * same CSS-transformed layer as the nodes, so it must be drawn in untransformed
 * coordinates or the zoom would be applied to the geometry twice.
 */
export function offsetWithin(el: HTMLElement, root: HTMLElement): { x: number; y: number } {
  let x = 0;
  let y = 0;
  let n: HTMLElement | null = el;
  while (n && n !== root) {
    x += n.offsetLeft;
    y += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  return { x, y };
}

export function FlowGraph({
  graph,
  networkIds = [],
  queues = [],
  clientQueueDepth,
}: {
  graph: FlowGraphData;
  networkIds?: string[];
  queues?: QueueDepthLike[];
  clientQueueDepth?: Map<string, number>;
}) {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [routesOn, setRoutesOn] = useState(false);
  // UX1 — which health states the graph is showing. 'all' is the off position.
  const [health, setHealth] = useState<HealthFilter>('all');
  // H37 — the drill-in node (its queue details show in a side panel).
  const [drill, setDrill] = useState<FlowNode | null>(null);

  // H37 — per-node task-queue summaries (queued / in-flight), aggregated up from per-tool rows.
  const qIndex = useMemo(() => queueIndex(queues), [queues]);
  const queueByNode = useMemo(() => {
    const m = new Map<string, NodeQueueSummary>();
    for (const n of graph.nodes) {
      const s = summarizeNodeQueue(graph, n.id, qIndex, clientQueueDepth);
      if (s.queued > 0 || s.running > 0 || (s.nodeTotal ?? 0) > 0) m.set(n.id, s);
    }
    return m;
  }, [graph, qIndex, clientQueueDepth]);
  const totalQueued = useMemo(() => queues.reduce((n, q) => n + q.depth, 0), [queues]);
  const totalRunning = useMemo(() => queues.reduce((n, q) => n + q.running, 0), [queues]);

  // Realtime call visualization: project live tasks (event bus) onto the graph. A node with an in-flight
  // task pulses (is-active); one with a recent failure is flagged (is-error). RUNNING tasks are inherently
  // current; failures are kept for a short recency window so the red flag fades after the error passes.
  const liveTasks = useStore((s) => s.tasks);
  const activityMap = useMemo(() => {
    const byCanon = new Map<string, ToolActivity>();
    const now = Date.now();
    const FAIL_WINDOW_MS = 60_000;
    for (const t of Object.values(liveTasks)) {
      const a = byCanon.get(t.canonical_tool) ?? { running: 0, failed: 0 };
      if (t.state === 'QUEUED' || t.state === 'DISPATCHED' || t.state === 'RUNNING') a.running += 1;
      else if ((t.state === 'FAILED' || t.state === 'REJECTED' || t.state === 'LOST') && now - t.ts < FAIL_WINDOW_MS)
        a.failed += 1;
      byCanon.set(t.canonical_tool, a);
    }
    return activityByNode(graph, byCanon);
  }, [liveTasks, graph]);
  const totalActive = useMemo(
    () => [...activityMap.values()].filter((a) => a.active).length,
    [activityMap],
  );

  const collapsible = useMemo(() => nodesWithChildren(graph), [graph]);
  const collapseVisible = useMemo(() => visibleNodeIds(graph, collapsed), [graph, collapsed]);
  const chain = useMemo(() => matchingChain(graph, query), [graph, query]);
  // UX1 — the health filter, alongside the name search. Both return "the ids to keep,
  // chains included" or null for no opinion, so composing them is a set intersection.
  const healthKeep = useMemo(
    () => healthChain(graph, health, activityMap),
    [graph, health, activityMap],
  );
  const counts = useMemo(() => healthCounts(graph, activityMap), [graph, activityMap]);

  const shownIds = useMemo(() => {
    const s = new Set<string>();
    for (const n of graph.nodes) {
      if (!collapseVisible.has(n.id)) continue;
      if (chain && !chain.has(n.id)) continue;
      if (healthKeep && !healthKeep.has(n.id)) continue;
      s.add(n.id);
    }
    return s;
  }, [graph.nodes, collapseVisible, chain, healthKeep]);

  // Routes overlay (H7): poll + live-react when the layer is on; map onto visible tool nodes.
  const routeUsages = useRouteUsages(networkIds, routesOn);
  const overlay = useMemo<RouteOverlay>(
    () => buildRouteOverlay(graph, routeUsages, shownIds),
    [graph, routeUsages, shownIds],
  );

  // Order each column by parent-barycenter so a network's whole subtree clusters together and the
  // connectors run mostly straight (a column-wide alphabetical sort scattered related nodes and made
  // the edges cross into noise). The ordering is recomputed only when the graph topology changes.
  const byColumn = useMemo(() => orderColumns(graph), [graph]);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Bulk fold controls: collapse EVERY node that has downstream (any edge source)
  // so the graph folds to its roots; expand clears the collapsed set entirely.
  const parentIds = useMemo(() => new Set(graph.edges.map((e) => e.from)), [graph.edges]);
  const collapseAll = useCallback(() => setCollapsed(new Set(parentIds)), [parentIds]);
  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const allCollapsed = parentIds.size > 0 && collapsed.size >= parentIds.size;


  // --- connector geometry (measured from the DOM) ---
  const innerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLDivElement>());
  const [edges, setEdges] = useState<{ key: string; d: string }[]>([]);
  const [routeEdges, setRouteEdges] = useState<
    { key: string; d: string; used: boolean; active: boolean; width: number; title: string }[]
  >([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const recompute = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const paths: { key: string; d: string }[] = [];
    for (const e of graph.edges) {
      if (!shownIds.has(e.from) || !shownIds.has(e.to)) continue;
      const a = nodeRefs.current.get(e.from);
      const b = nodeRefs.current.get(e.to);
      if (!a || !b) continue;
      const pa = offsetWithin(a, inner);
      const pb = offsetWithin(b, inner);
      const x1 = pa.x + a.offsetWidth;
      const y1 = pa.y + a.offsetHeight / 2;
      const x2 = pb.x;
      const y2 = pb.y + b.offsetHeight / 2;
      const mx = (x1 + x2) / 2;
      paths.push({ key: `${e.from}->${e.to}`, d: `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}` });
    }
    setEdges(paths);

    // Route overlay segments: both endpoints are tool nodes (same column, stacked), so bow the
    // connector out to the RIGHT of the column. Thickness scales with usage (clamped).
    const rpaths: typeof routeEdges = [];
    if (routesOn) {
      const maxN = Math.max(1, overlay.maxInstantiations);
      for (const e of overlay.edges) {
        const a = nodeRefs.current.get(e.fromId);
        const b = nodeRefs.current.get(e.toId);
        if (!a || !b) continue;
        const pa = offsetWithin(a, inner);
        const pb = offsetWithin(b, inner);
        const x1 = pa.x + a.offsetWidth;
        const y1 = pa.y + a.offsetHeight / 2;
        const x2 = pb.x + b.offsetWidth;
        const y2 = pb.y + b.offsetHeight / 2;
        const span = Math.abs(y2 - y1);
        const bulge = 40 + Math.min(120, span * 0.4);
        const d = `M ${x1} ${y1} C ${x1 + bulge} ${y1}, ${x2 + bulge} ${y2}, ${x2} ${y2}`;
        const width = e.used ? 2 + Math.round((e.instantiations / maxN) * 5) : 1.5;
        rpaths.push({
          key: e.key,
          d,
          used: e.used,
          active: e.active,
          width,
          title: `${e.routeName} · ${e.instantiations} use${e.instantiations === 1 ? '' : 's'}${e.active ? ' · active' : ''}`,
        });
      }
    }
    setRouteEdges(rpaths);
    setSize({ w: inner.scrollWidth, h: inner.scrollHeight });
  }, [graph.edges, shownIds, routesOn, overlay]);

  // Re-measure after every layout-affecting change (filter / collapse / data / routes).
  useLayoutEffect(recompute, [recompute, collapsed, query]);

  // Re-measure on container resize (card wrapping) and window resize.
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => recompute());
      ro.observe(inner);
    }
    window.addEventListener('resize', recompute);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [recompute]);

  // H46 — the scroll area no longer stretches to the viewport bottom (the H6 fit
  // logic was removed). It has a fixed max height from CSS (.flow-scroll) like any
  // other Overview block; the inner graph scrolls within it.
  const scrollRef = useRef<HTMLDivElement>(null);

  const setNodeRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) nodeRefs.current.set(id, el);
      else nodeRefs.current.delete(id);
    },
    [],
  );

  const totalShown = shownIds.size;

  return (
    <div className="flow">
      <div className="flow-toolbar">
        <input
          className="flow-search"
          type="search"
          placeholder="Filter nodes by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter graph nodes by name"
        />
        {query && (
          <button className="btn btn-sm" onClick={() => setQuery('')}>
            Clear
          </button>
        )}
        {/* UX1 — health chips. The graph could already SAY a node was in trouble (a red
            flag, a dimmed card); it could not answer "show me those". Each chip carries
            its own count, so the answer is legible before anything is clicked — and a
            zero on Failed is the reading an operator actually wants most mornings. */}
        <span className="flow-health" role="group" aria-label="Filter by node health">
          {HEALTH_FILTERS.map((h) => (
            <button
              key={h}
              className={`chip filter-chip flow-health-chip is-${h} ${health === h ? 'active' : ''}`}
              aria-pressed={health === h}
              data-testid={`flow-health-${h}`}
              title={HEALTH_TITLE[h]}
              onClick={() => setHealth((prev) => (prev === h && h !== 'all' ? 'all' : h))}
            >
              {HEALTH_LABEL[h]}
              {h !== 'all' && <span className="flow-health-count"> {counts[h]}</span>}
            </button>
          ))}
        </span>
        {parentIds.size > 0 && (
          <button
            className="btn btn-sm"
            onClick={allCollapsed ? expandAll : collapseAll}
            title={allCollapsed ? 'Expand every node' : 'Collapse every node to its roots'}
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        )}
        <label className="flow-routes-toggle" title="Overlay stream/call routes with live usage">
          <input
            type="checkbox"
            checked={routesOn}
            onChange={(e) => setRoutesOn(e.target.checked)}
            aria-label="Toggle routes layer"
          />
          <span>Routes</span>
        </label>
        {totalActive > 0 && (
          <span className="flow-live-summary" title="Live calls in flight right now (event bus)">
            <span className="chip chip-live-active">● {totalActive} active</span>
          </span>
        )}
        {(totalQueued > 0 || totalRunning > 0) && (
          <span className="flow-queue-summary" title="Task queues across the graph — click a node for details">
            <span className="chip chip-queue">⏳ {totalQueued} queued</span>
            <span className="chip chip-running">▶ {totalRunning} in-flight</span>
          </span>
        )}
        <span className="flow-hint">
          {totalShown} of {graph.nodes.length} nodes
        </span>
      </div>

      {routesOn && (
        <div className="flow-routes-legend" role="note">
          <span className="rl-title">Routes layer</span>
          <span className="rl-item">
            <span className="rl-swatch rl-used" /> used (thicker = more uses)
          </span>
          <span className="rl-item">
            <span className="rl-swatch rl-active" /> active now
          </span>
          <span className="rl-item">
            <span className="rl-swatch rl-unused" /> unused
          </span>
          <span className="rl-count">
            {overlay.usedRouteCount}/{overlay.routeCount} routes used
          </span>
        </div>
      )}

      <div className="flow-scroll" ref={scrollRef} data-testid="flow-scroll">
        <div className="flow-inner" ref={innerRef}>
          <svg
            className="flow-edges"
            width={size.w || undefined}
            height={size.h || undefined}
            aria-hidden="true"
          >
            {edges.map((e) => (
              <path key={e.key} className="flow-edge" d={e.d} fill="none" />
            ))}
            {routesOn &&
              routeEdges.map((e) => (
                <path
                  key={e.key}
                  className={`flow-route-edge ${e.used ? 'is-used' : 'is-unused'} ${e.active ? 'is-active' : ''}`}
                  d={e.d}
                  fill="none"
                  strokeWidth={e.width}
                >
                  <title>{e.title}</title>
                </path>
              ))}
          </svg>

          <div className="flow-columns">
            {COLUMNS.map((col) => {
              const all = byColumn.get(col.key) ?? [];
              const shown = all.filter((n) => shownIds.has(n.id));
              return (
                <div className="flow-col" key={col.key}>
                  <div className="flow-col-head">
                    {col.rel && <span className="flow-rel">{col.rel} →</span>}
                    <span className="flow-col-title">{col.title}</span>
                    <span className="flow-col-count">
                      {shown.length}/{all.length}
                    </span>
                  </div>
                  <div className="flow-col-body">
                    {all.length === 0 ? (
                      <div className="flow-empty">No {col.title.toLowerCase()}</div>
                    ) : shown.length === 0 ? (
                      <div className="flow-empty">No matches</div>
                    ) : (
                      shown.map((n, idx) => {
                        const canCollapse = collapsible.has(n.id);
                        const isCollapsed = collapsed.has(n.id);
                        const onRoute = routesOn && overlay.usedNodeIds.has(n.id);
                        const q = queueByNode.get(n.id);
                        const act: NodeActivity | undefined = activityMap.get(n.id);
                        // Visual grouping: nodes are barycenter-ordered so a network's subtree is
                        // contiguous; mark the first node of each new network-group (except the very
                        // first) so CSS can draw a thin separator — making the per-network grouping read.
                        const groupStart =
                          idx > 0 && col.key !== 'networks' && (n.networkId ?? '∅') !== (shown[idx - 1].networkId ?? '∅');
                        return (
                          <div
                            key={n.id}
                            ref={setNodeRef(n.id)}
                            className={`flow-node ${toneClass(n.tone)} ${isCollapsed ? 'is-collapsed' : ''} ${onRoute ? 'on-route' : ''} ${q ? 'has-queue' : ''} ${groupStart ? 'flow-group-start' : ''} ${n.disabled ? 'is-disabled-node' : ''} ${act?.active ? 'is-active' : ''} ${act?.error ? 'is-error' : ''} flow-node-clickable`}
                            role="button"
                            tabIndex={0}
                            onClick={() => setDrill(n)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setDrill(n);
                              }
                            }}
                            title={
                              (act?.error ? `${act.failed} recent failure(s) — ` : '') +
                              (act?.active ? `${act.running} call(s) in flight — ` : '') +
                              (n.disabled ? 'not currently running (no live provider) — ' : '') +
                              'click for details'
                            }
                          >
                            <span className="flow-node-bar" />
                            <div className="flow-node-text">
                              <div className="flow-node-label" title={n.label}>
                                {n.label}
                              </div>
                              {n.sublabel && (
                                <div className="flow-node-sub" title={n.sublabel}>
                                  {n.sublabel}
                                </div>
                              )}
                              {q && (
                                <div className="flow-node-queue" aria-label="task queue">
                                  {q.queued > 0 && <span className="chip chip-queue">⏳ {q.queued}</span>}
                                  {q.running > 0 && <span className="chip chip-running">▶ {q.running}</span>}
                                  {q.nodeTotal != null && q.nodeTotal > 0 && q.queued === 0 && (
                                    <span className="chip chip-queue">⏳ {q.nodeTotal}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {canCollapse && (
                              <button
                                className="flow-node-toggle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle(n.id);
                                }}
                                aria-expanded={!isCollapsed}
                                title={isCollapsed ? 'Expand downstream' : 'Collapse downstream'}
                              >
                                {isCollapsed ? '+' : '−'}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {drill && (
        <NodeQueueDrawer
          node={drill}
          summary={summarizeNodeQueue(graph, drill.id, qIndex, clientQueueDepth)}
          activity={activityMap.get(drill.id)}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

// H37 drill-in: a side panel with the task-queue breakdown for the clicked node — totals plus the
// contributing per-tool rows (queued depth, in-flight, providers, oldest wait).
function NodeQueueDrawer({
  node,
  summary,
  activity,
  onClose,
}: {
  node: FlowNode;
  summary: NodeQueueSummary;
  activity?: NodeActivity;
  onClose: () => void;
}) {
  const colTitle =
    node.column === 'networks'
      ? 'Network'
      : node.column === 'clients'
        ? 'Client (in network)'
        : node.column === 'services'
          ? 'Service'
          : 'Tool';
  const hasRows = summary.rows.length > 0;
  return (
    <Drawer
      title={node.label}
      subtitle={<span className="muted small">{colTitle}{node.sublabel ? ` · ${node.sublabel}` : ''}</span>}
      onClose={onClose}
    >
      {node.disabled && (
        <div className="banner muted small" role="note">
          Not currently running — no live provider. An agent can still discover/request this; it comes up
          when a node serves it.
        </div>
      )}
      {activity?.active && (
        <DetailRow label="Live calls now"><b className="tone-ok">{activity.running} in flight</b></DetailRow>
      )}
      {activity?.error && (
        <DetailRow label="Recent failures">
          <b className="form-error">{activity.failed}</b>
          <span className="muted small"> · open the Activity panel below for the error message + payload</span>
        </DetailRow>
      )}
      <DetailRow label="Queued (depth)"><b>{summary.queued}</b></DetailRow>
      <DetailRow label="In-flight (running)"><b>{summary.running}</b></DetailRow>
      {summary.nodeTotal != null && (
        <DetailRow label="Node queue total">
          <span title="Per-node total reported by the gateway (Client.queue_depth)">{summary.nodeTotal}</span>
        </DetailRow>
      )}
      <div>
        <h4>Per-tool queues</h4>
        {hasRows ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tool</th>
                <th style={{ textAlign: 'right' }}>Queued</th>
                <th style={{ textAlign: 'right' }}>Running</th>
                <th style={{ textAlign: 'right' }}>Providers</th>
                <th style={{ textAlign: 'right' }}>Oldest wait</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr key={r.canonical_tool}>
                  <td><code className="mono small">{r.canonical_tool}</code></td>
                  <td style={{ textAlign: 'right' }}>{r.depth}</td>
                  <td style={{ textAlign: 'right' }}>{r.running}</td>
                  <td style={{ textAlign: 'right' }}>{r.providers}</td>
                  <td style={{ textAlign: 'right' }}>{r.oldest_wait_s != null ? `${r.oldest_wait_s}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted small">
            No active task queues for this node.
            {node.column === 'clients' && ' Per-network queue split is not exposed by the gateway API; the node total above is the per-client figure.'}
          </p>
        )}
      </div>
    </Drawer>
  );
}
